import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts';
import { LanguageSelector } from '../components/common';
import { ProfileCard, ScoreBar, CareerList, CtaButton } from '../components/result';
import { ProfileResult } from '../types/result';
import { submitQuiz } from '../services/api';

interface ResultLocationState {
  result: ProfileResult;
  answers?: string[];
}

const ResultPage: React.FC = () => {
  const { language, isRTL } = useLanguage();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as ResultLocationState | undefined;
  const initialResult = state?.result;
  const answers = state?.answers;
  const [result, setResult] = React.useState<ProfileResult | null>(initialResult ?? null);
  const [loading, setLoading] = React.useState(false);

  // If no result in state, redirect home
  React.useEffect(() => {
    if (!initialResult) {
      navigate('/');
    }
  }, [initialResult, navigate]);

  React.useEffect(() => {
    const refreshLocalizedResult = async () => {
      if (!initialResult) return;

      if (initialResult.language === language) {
        setResult(initialResult);
        return;
      }

      if (!answers || !answers.length) {
        setResult(initialResult);
        return;
      }

      setLoading(true);
      try {
        const localized = await submitQuiz({
          answers,
          language,
        });
        setResult(localized);
      } catch {
        setResult(initialResult);
      } finally {
        setLoading(false);
      }
    };

    void refreshLocalizedResult();
  }, [answers, initialResult, language]);

  if (!result) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-gray-500 text-sm font-medium">{t('common.loading')}</p>
      </div>
    );
  }

  const handleRestart = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Language selector */}
      <div className="flex justify-end p-4">
        <LanguageSelector />
      </div>

      {/* Result content */}
      <div className="max-w-lg mx-auto px-4 pb-12 flex flex-col gap-4">
        {/* Header */}
        <div className="w-full flex flex-col items-center text-center">
          <img src="/logo.jpeg" alt="Fast Profile" className="w-16 h-16 rounded-xl mb-2" />
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
            {t('result.brand')}
          </p>
        </div>

        <ProfileCard result={result} />
        <ScoreBar result={result} />
        <CareerList result={result} />
        <CtaButton result={result} onRestart={handleRestart} />
      </div>
    </div>
  );
};

export default ResultPage;
